import { Sparkles, Layers, Zap, Lock, ArrowRight, Crown } from "lucide-react";
import { Button } from "./ui/button";

const templates = [
  {
    id: "clasica",
    name: "Clásica",
    tag: "Disponible en todos los planes",
    description: "Diseño limpio y profesional. Perfecto para presentar tu propiedad de forma clara y directa.",
    accent: "#a033fd",
    accentBg: "from-purple-50 to-white",
    available: "all",
    icon: Layers,
    preview: (
      <div className="absolute inset-0 bg-white p-3 flex flex-col gap-2">
        <div className="h-2 bg-gradient-to-r from-purple-200 to-purple-300 rounded w-1/2" />
        <div className="aspect-[16/9] rounded-md bg-gradient-to-br from-purple-300 via-pink-200 to-orange-200 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
          <div className="absolute bottom-1 left-2 right-2 flex gap-0.5">
            {[...Array(5)].map((_, i) => <div key={i} className="h-1 flex-1 bg-white/60 rounded" />)}
          </div>
        </div>
        <div className="flex gap-1.5 mt-1">
          <div className="flex-1 h-3 bg-purple-100 rounded" />
          <div className="flex-1 h-3 bg-purple-100 rounded" />
          <div className="flex-1 h-3 bg-purple-100 rounded" />
        </div>
        <div className="space-y-1">
          <div className="h-1.5 bg-gray-200 rounded w-full" />
          <div className="h-1.5 bg-gray-200 rounded w-4/5" />
          <div className="h-1.5 bg-gray-200 rounded w-3/5" />
        </div>
        <div className="mt-auto h-5 rounded bg-purple-500" />
      </div>
    )
  },
  {
    id: "moderna",
    name: "Moderna",
    tag: "Plan Pro en adelante",
    description: "Layout asimétrico de dos columnas con galería interactiva. Ideal para propiedades premium con muchas fotos.",
    accent: "#3b82f6",
    accentBg: "from-blue-50 to-white",
    available: "pro",
    icon: Zap,
    preview: (
      <div className="absolute inset-0 bg-white p-3 flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <div className="h-2 bg-gray-300 rounded w-1/3" />
          <div className="h-3 w-12 bg-blue-500 rounded-full" />
        </div>
        <div className="grid grid-cols-5 gap-1.5 flex-1">
          <div className="col-span-3 rounded-md bg-gradient-to-br from-blue-300 via-cyan-200 to-blue-400 relative overflow-hidden">
            <div className="absolute bottom-1 right-1 px-1 h-2 bg-black/40 rounded text-[6px] text-white flex items-center">1/8</div>
          </div>
          <div className="col-span-2 flex flex-col gap-1">
            <div className="h-2.5 bg-blue-500 rounded w-full" />
            <div className="h-1 bg-gray-300 rounded w-3/4" />
            <div className="grid grid-cols-2 gap-0.5 mt-1">
              <div className="h-3 bg-gray-100 rounded" />
              <div className="h-3 bg-gray-100 rounded" />
              <div className="h-3 bg-gray-100 rounded" />
              <div className="h-3 bg-gray-100 rounded" />
            </div>
            <div className="mt-auto h-4 bg-blue-500 rounded" />
          </div>
        </div>
      </div>
    )
  },
  {
    id: "elegante",
    name: "Elegante",
    tag: "Plan Pro en adelante",
    description: "Hero a pantalla completa con tema oscuro inmersivo. Para propiedades de lujo que buscan impacto visual.",
    accent: "#f59e0b",
    accentBg: "from-amber-50 to-white",
    available: "pro",
    icon: Sparkles,
    preview: (
      <div className="absolute inset-0 bg-gray-950 flex flex-col">
        <div className="aspect-[16/10] relative bg-gradient-to-br from-amber-900 via-amber-700 to-stone-800 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/40 to-transparent" />
          <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-black/40 rounded text-[6px] text-white border border-white/20">Compartir</div>
          <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-black/40 rounded text-[6px] text-white">1/12</div>
          <div className="absolute bottom-2 left-2 right-2">
            <div className="flex gap-1 mb-1">
              <div className="px-1.5 py-0.5 bg-amber-500 rounded text-[6px] text-white font-bold">EN VENTA</div>
              <div className="px-1.5 py-0.5 bg-white/10 backdrop-blur-sm rounded text-[6px] text-white border border-white/20">Casa</div>
            </div>
            <div className="h-2.5 bg-white rounded w-3/4 mb-0.5" />
            <div className="h-1.5 bg-amber-400 rounded w-2/5" />
          </div>
        </div>
        <div className="px-2 py-1.5 border-y border-white/10 bg-gray-900 flex justify-around">
          <div className="h-2 w-3 bg-amber-500/40 rounded" />
          <div className="h-2 w-3 bg-amber-500/40 rounded" />
          <div className="h-2 w-3 bg-amber-500/40 rounded" />
          <div className="h-2 w-3 bg-amber-500/40 rounded" />
        </div>
        <div className="flex-1 p-2 flex flex-col gap-1">
          <div className="h-1 bg-white/20 rounded w-3/4" />
          <div className="h-1 bg-white/20 rounded w-full" />
          <div className="h-1 bg-white/20 rounded w-1/2" />
        </div>
      </div>
    )
  }
];

export const Templates = () => {
  return (
    <section id="plantillas" className="py-24 bg-white relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-brand-purple/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-brand-pink/5 rounded-full blur-3xl pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-1.5 bg-gradient-to-r from-brand-purple/10 to-brand-pink/10 text-brand-purple px-4 py-1.5 rounded-full text-sm font-medium mb-4 border border-brand-purple/20">
            <Layers className="w-4 h-4" />
            3 Plantillas profesionales
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Elige el diseño que <span className="text-brand-purple">vende</span> más
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Cambia entre 3 plantillas distintas con un solo click. Cada propiedad puede tener su propio estilo.
          </p>
        </div>

        {/* Templates Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-12" data-testid="templates-grid">
          {templates.map((tpl) => {
            const Icon = tpl.icon;
            const isPro = tpl.available === "pro";
            return (
              <div 
                key={tpl.id}
                className="group relative rounded-2xl border border-gray-200 bg-white overflow-hidden hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                data-testid={`template-card-${tpl.id}`}
              >
                {/* Pro Badge */}
                {isPro && (
                  <div className="absolute top-3 right-3 z-10 flex items-center gap-1 bg-gradient-to-r from-amber-400 to-amber-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-lg">
                    <Crown className="w-3 h-3" />
                    PRO+
                  </div>
                )}

                {/* Preview frame */}
                <div className={`relative aspect-[4/3] overflow-hidden bg-gradient-to-br ${tpl.accentBg} border-b border-gray-100`}>
                  {/* Browser bar */}
                  <div className="absolute top-0 left-0 right-0 h-5 bg-gray-100/80 backdrop-blur-sm flex items-center px-2 gap-1 z-20 border-b border-gray-200">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-300" />
                    <div className="w-1.5 h-1.5 rounded-full bg-yellow-300" />
                    <div className="w-1.5 h-1.5 rounded-full bg-green-300" />
                  </div>
                  <div className="absolute top-5 left-0 right-0 bottom-0">
                    {tpl.preview}
                  </div>
                </div>

                {/* Card content */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-9 h-9 rounded-lg flex items-center justify-center"
                        style={{ background: `${tpl.accent}15` }}
                      >
                        <Icon className="w-5 h-5" style={{ color: tpl.accent }} />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 text-lg leading-tight">{tpl.name}</h3>
                        <p className={`text-xs ${isPro ? 'text-amber-700' : 'text-green-700'} font-medium`}>
                          {tpl.tag}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">{tpl.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footnote + CTA */}
        <div className="flex flex-col items-center text-center gap-4">
          <div className="inline-flex items-center gap-2 text-gray-500 text-sm">
            <Lock className="w-4 h-4" />
            <span>Plantillas <strong className="text-amber-700">Moderna</strong> y <strong className="text-amber-700">Elegante</strong> disponibles desde el plan <strong>Pro</strong></span>
          </div>
          <Button
            size="lg"
            className="bg-brand-purple hover:bg-brand-pink text-white px-8 text-base"
            onClick={(e) => {
              e.preventDefault();
              if (window.firePixelAndNavigate) {
                window.firePixelAndNavigate('Lead', { content_name: 'CTA Templates' }, '/precios');
              } else {
                window.location.href = '/precios';
              }
            }}
            data-testid="templates-cta-btn"
          >
            Ver planes y desbloquear
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </section>
  );
};
